/**
 * Auto-Fill Hook
 * Custom hook for managing auto-fill functionality across forms
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getAutoFillSuggestions } from '../utils/autoFillUtils';

// Simple 2-character threshold for all fields
const getMinimumThreshold = (fieldName, dataType) => {
    return 2; // Always use 2 characters as minimum threshold
};

export const useAutoFill = (existingData = [], validationRules = {}, dataType = 'generic') => {
    const [suggestions, setSuggestions] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const debounceTimeoutRef = useRef(null);
    const lastFieldRef = useRef(null);

    // Debounced auto-fill function
    const debouncedAutoFill = useCallback((fieldName, fieldValue, currentFormData) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (fieldValue && fieldValue.trim().length >= 2) {
                setIsLoading(true);

                try {
                    const autoFillSuggestions = getAutoFillSuggestions(
                        fieldName,
                        fieldValue,
                        existingData,
                        currentFormData,
                        validationRules,
                        dataType
                    );

                    setSuggestions(autoFillSuggestions);
                    setShowSuggestions(Object.keys(autoFillSuggestions).length > 0);
                    setCurrentField(fieldName);
                    lastFieldRef.current = fieldName;
                } catch (error) {
                    console.error('Auto-fill error:', error);
                    setSuggestions({});
                    setShowSuggestions(false);
                } finally {
                    setIsLoading(false);
                }
            } else {
                // Only clear suggestions if the field is empty, not when switching fields
                if (!fieldValue || fieldValue.trim().length === 0) {
                    setSuggestions({});
                    setShowSuggestions(false);
                    setCurrentField(null);
                }
            }
        }, 300); // 300ms debounce
    }, [existingData, validationRules, dataType]);

    // Check if field should trigger autofill
    const shouldTriggerAutoFill = useCallback((fieldName, fieldValue) => {
        // Skip if value is too short - use dynamic threshold
        const minThreshold = getMinimumThreshold(fieldName, dataType);
        if (!fieldValue || fieldValue.trim().length < minThreshold) {
            return false;
        }

        // Skip non-text fields
        const skipFields = [
            'checkbox', 'radio', 'select', 'switch', 'toggle',
            'is_active', 'is_staff', 'is_superuser', 'is_approved',
            'renewal', 'release_to_third_parties', 'publish_on_website',
            'agreed_to_conditions', 'email_subscribed', 'show_in_external_view',
            'allow_distributor_access', 'send_to_xero', 'send_email'
        ];

        // Check if field name contains skip patterns
        const shouldSkip = skipFields.some(skipField =>
            fieldName.toLowerCase().includes(skipField.toLowerCase())
        );

        return !shouldSkip;
    }, [dataType]);

    // Handle field change with auto-fill
    const handleFieldChange = useCallback((fieldName, fieldValue, currentFormData, onFormChange) => {
        // Call the original form change handler
        if (onFormChange) {
            onFormChange(fieldName, fieldValue);
        }

        // Only trigger auto-fill for text-based fields
        if (shouldTriggerAutoFill(fieldName, fieldValue)) {
            debouncedAutoFill(fieldName, fieldValue, currentFormData);
        }
    }, [debouncedAutoFill, shouldTriggerAutoFill]);


    // Apply specific suggestion
    const applySuggestion = useCallback((field, value, onFormChange) => {
        if (onFormChange) {
            onFormChange(field, value);
        }

        // Remove this suggestion from the list
        setSuggestions(prev => {
            const newSuggestions = { ...prev };
            const { [field]: removed, ...rest } = newSuggestions;

            // Hide suggestions if no more suggestions
            if (Object.keys(rest).length === 0) {
                setShowSuggestions(false);
            }

            return rest;
        });
    }, []);

    // Clear all suggestions
    const clearSuggestions = useCallback(() => {
        setSuggestions({});
        setShowSuggestions(false);
        setCurrentField(null);
    }, []);

    // Clear suggestions when user clicks outside or wants to dismiss
    const dismissSuggestions = useCallback(() => {
        setShowSuggestions(false);
    }, []);


    // Update existing data (useful when data changes)
    const updateExistingData = useCallback((newData) => {
        // Clear current suggestions when data changes
        setSuggestions({});
        setShowSuggestions(false);
        setCurrentField(null);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
        };
    }, []);

    return {
        // State
        suggestions,
        isLoading,
        showSuggestions,
        currentField,

        // Actions
        handleFieldChange,
        applySuggestion,
        clearSuggestions,
        dismissSuggestions,
        updateExistingData
    };
};

export default useAutoFill;
